import { createElement, useDeferredValue, useRef } from "react"
import type { CSSProperties, ReactNode, RefObject } from "react"

import { useScroll } from "react-use"
import type { State as ScrollState } from "react-use/lib/useScroll"

import { EAxisDirection, buildCssClassName, getTransitionTwClasses } from "@/3514/utils"

type TParams =
    | { isEnabled: boolean; height: CSSProperties["height"]; width?: never; direction: EAxisDirection.Vertical }
    | { isEnabled: boolean; height?: never; width: CSSProperties["width"]; direction: EAxisDirection.Horizontal }
type TReturn = { element: ReactNode; parentRef: RefObject<HTMLDivElement> }

const sentinelClasses = ({
    isVisible,
    direction
}: {
    isVisible: boolean
    direction: EAxisDirection.Horizontal | EAxisDirection.Vertical
}): string =>
    buildCssClassName(
        "block",
        "z-[1]",
        "absolute",
        "bottom-0",
        "w-full",
        "to-transparent",
        "pointer-events-none",
        { "w-full bg-gradient-to-t right-0 left-0": direction === EAxisDirection.Vertical },
        { "h-full bg-gradient-to-l right-0 left-auto": direction === EAxisDirection.Horizontal },
        { "opacity-[0]": isVisible },
        { "opacity-[1]": !isVisible },
        { "from-white": import.meta.env.PROD },
        { "from-blue-300": import.meta.env.DEV },
        getTransitionTwClasses("all", 200)
    )

function useListSentinel({
    isEnabled,
    height,
    width,
    direction = EAxisDirection.Vertical
}: TParams): ReturnType<(params: TParams) => TReturn> {
    const parentRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const { y: parentYScroll, x: parentXScroll }: ScrollState = useScroll(parentRef)

    const deferredParentYScroll: number = useDeferredValue<number>(parentYScroll)
    const deferredParentXScroll: number = useDeferredValue<number>(parentXScroll)

    return {
        element: createElement("div", {
            style: { height, width },
            hidden: !isEnabled,
            className: sentinelClasses({
                direction,
                isVisible:
                    direction === EAxisDirection.Horizontal ? deferredParentXScroll > 0 : deferredParentYScroll > 0
            })
        }),
        parentRef
    }
}

export { useListSentinel }
export type { TReturn as TUseListSentinel }
